import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";

// Material-UI imports
import Grid from "@mui/material/Grid";

// MSAL imports
import { MsalProvider, useIsAuthenticated } from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";
import { CustomNavigationClient } from "./utils/NavigationClient";

// App imports
import { PageLayout } from "./ui-components/PageLayout";
import WelcomeWalkthrough from "./ui-components/WelcomeWalkthrough";
import { WelcomeProvider, useWelcome } from "./context/WelcomeContext";
import AnimatedRoutes from "./ui-components/AnimatedRoutes";
import SplashScreen from "./ui-components/SplashScreen";

// Styles
import "./styles/App.scss";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/effect-creative";
import "./fonts/fselliotpro-regular-webfont.ttf";
import "./fonts/FSElliotPro-Bold.ttf";
import "./fonts/fselliotpro-heavy-webfont.ttf";

type AppProps = {
  pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
  // The next 3 lines are optional. This is how you configure MSAL to take advantage of the router's navigate functions when MSAL redirects between pages in your app
  const navigate = useNavigate();
  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);

  return (
    <MsalProvider instance={pca}>
      <WelcomeProvider>
        <AppContent />
      </WelcomeProvider>
    </MsalProvider>
  );
}

function AppContent() {
  const { showWelcome, handleCompleteWelcome } = useWelcome();
  const [loading, setLoading] = useState(true);
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence mode="wait">
      {loading && isAuthenticated ? (
        <SplashScreen />
      ) : showWelcome ? (
        <WelcomeWalkthrough value={{ showWelcome, handleCompleteWelcome }} />
      ) : (
        <PageLayout key="main">
            <AnimatedRoutes />
        </PageLayout>
      )}
    </AnimatePresence>
  );
}

export default App;
